import { MotifIcon } from "@ey-xd/motif-react";
import { IconoirList } from "@ey-xd/motif-icon";
import "./OrdersTable.scss";
import { ProductAccordian } from "../../../pages/Checkout/Components/OrderExpected/ProductAccordian";
import orderEmptyImg from "../../../assets/images/empty_order_pipeline.svg";
import EmptyOrder from "../../EmptyOrder";
import {
  COST_MACRO_CATEGORY,
  NO_ORDERS_HEADING,
  NO_ORDERS_TEXT,
} from "../../../Constant";
import { getTotalOrderAmount } from "../helper";
import { RichTextEditor } from "../../Form/RichTextEditor/RichTextEditor";
import OrderItemsHeaderTooltip from "../OrderItemsHeaderTooltip/OrderItemsHeaderTooltip";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../store";
import { fetchSkuConfigData } from "../../../store/slices/CTDBSkuConfigSlice";
import Loader from "../../Loader/Loader";
import NoSearchResult from "../../NoSearchResult/NoSearchResult";
import React, { useRef, useEffect, useState } from "react";
import { sanitizeHTMLSpace } from "../../../utils/common";

export interface ColumnConfig {
  key: string;
  label: string;
  render?: (row: any) => React.ReactNode;
}

interface OrdersTableProps {
  data: any[];
  columnsData: ColumnConfig[];
  setOrders: React.Dispatch<React.SetStateAction<any[]>>;
  setShowPagination: React.Dispatch<React.SetStateAction<boolean>>;
  searchText: string;
  isFilterApplied: boolean;
}

const OrdersTable: React.FC<OrdersTableProps> = ({
  data,
  columnsData,
  setOrders,
  setShowPagination,
  searchText,
  isFilterApplied,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const configData = useSelector(
    (state: RootState) => state.CTDBSkuConfigSlice.data,
  );

  const ordersTableContainerRef = useRef<HTMLDivElement>(null);
  const tableScrollRef = useRef<HTMLDivElement>(null);
  const tableHeaderRef = useRef<HTMLTableSectionElement>(null);
  const [expandedTableWidth, setExpandedTableWidth] = useState<number>(0);

  useEffect(() => {
    const handleScroll = () => {
      if (!ordersTableContainerRef.current || !tableHeaderRef.current) return;
      const tableTop =
        ordersTableContainerRef.current.getBoundingClientRect().top;
      const offset = Math.abs(Math.min(0, tableTop));
      tableHeaderRef.current.style.transform = `translateY(${offset - 1}px)`;
    };

    window.addEventListener("scroll", handleScroll);
    window.addEventListener("resize", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleScroll);
    };
  }, []);

  useEffect(() => {
    const updateExpandedTableWidth = () => {
      if (!tableScrollRef.current) return;
      setExpandedTableWidth(tableScrollRef.current.clientWidth);
    };

    updateExpandedTableWidth();
    window.addEventListener("resize", updateExpandedTableWidth);

    const resizeObserver = new ResizeObserver(updateExpandedTableWidth);
    if (tableScrollRef.current) {
      resizeObserver.observe(tableScrollRef.current);
    }

    return () => {
      window.removeEventListener("resize", updateExpandedTableWidth);
      resizeObserver.disconnect();
    };
  }, [data, columnsData, configData]);

  const renderNoSearchResult = () => {
    if (isFilterApplied) {
      const header = searchText
        ? `No result under the filter and by the keyword "${searchText}"`
        : `No result under the filter`;

      const message = searchText
        ? "Did you not find any results? We recommend refining your search criteria and/or broadening your filter parameters to enhance your search outcomes."
        : "Did not find any results? We recommend broadening your filter criteria for a more comprehensive search.";

      return (
        <NoSearchResult
          searchValue={searchText}
          header={header}
          message={message}
        />
      );
    } else {
      return <NoSearchResult searchValue={searchText} />;
    }
  };

  if (Array.isArray(data) && data?.length === 0) {
    if (!searchText && !isFilterApplied) {
      return (
        <EmptyOrder
          image={orderEmptyImg}
          header={NO_ORDERS_HEADING}
          subHeader={NO_ORDERS_TEXT}
        />
      );
    }
    return renderNoSearchResult();
  } else {
    if (configData === null && data && data.length > 0) {
      dispatch(fetchSkuConfigData());
    }

    if (!configData) {
      return <Loader />;
    } else {
      setShowPagination(true);
    }

    const { CostCategory, CostMacroCategory } = configData;

    const getCostCataegoryType = (order: any) => {
      if (CostCategory.length === 0) {
        return null;
      }
      return CostCategory.find(
        (cost) => cost.CostMetricCd === order.CostMetricCd,
      )?.CostMetricType;
    };
    const getCostMetric = (order: any) => {
      if (CostCategory.length === 0) {
        return null;
      }
      return CostCategory.find(
        (cost) => cost.CostMetricCd === order.CostMetricCd,
      )?.CostMetric;
    };

    const getCostMacroCategory = (order: any) => {
      if (CostMacroCategory.length === 0) {
        return null;
      }
      return CostMacroCategory.filter((cost) =>
        order.CostMacroCategoryCd.includes(cost.CostMacroCategoryCd),
      )
        .map((item) => item.Name)
        .join(", ");
    };

    const getClassNamesOrdersTable = (key: string) => {
      switch (key) {
        case "SKUName":
          return "orders-sku-name";
        case "OrderName":
          return "orders-order-name";
        case "EngName":
          return "orders-eng-name";
        case "RequestorId":
          return "orders-requestor-id";
        case "ReqDate":
          return "orders-req-date";
        case "Total":
          return "orders-total";
        case "StatusCd":
          return "orders-status-cd";
        case "OrderSkuStatus":
          return "orders-order-sku-status";
        case "Completion Date":
        case "CompletionDate":
          return "orders-completion-date";
        case "action":
        case "Action":
          return "orders-actions";
        default:
          return "";
      }
    };

    return (
      <div className="orders-table-container" ref={ordersTableContainerRef}>
        <div className="table-scroll-wrapper" ref={tableScrollRef}>
          <table className="motif-table-standard">
            <thead className="fixed-header-wrapper" ref={tableHeaderRef}>
              <tr>
                {columnsData.map((col) => (
                  <th
                    key={col.key}
                    className={getClassNamesOrdersTable(col.key)}
                  >
                    {col.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.map((order) => (
                <React.Fragment key={order.OrderSkuId}>
                  <tr>
                    {columnsData.map((col) => (
                      <td key={col.key}>
                        {col.render ? col.render(order) : order[col.key]}
                      </td>
                    ))}
                  </tr>

                  {/* SKU Details */}
                  {order.isSKUExpanded && (
                    <tr className="product-list-wrapper">
                      <td colSpan={columnsData.length}>
                        <div className="product-list order-success-content">
                          <div className="product-list-header">
                            <MotifIcon
                              iconFunction={IconoirList}
                              size="20"
                              title="Order Bundle"
                            />
                            <span>SKU Details</span>
                          </div>
                          <div className="sku-details-content">
                            <div className="sku-details-desc-heading">
                              Description
                            </div>
                            <RichTextEditor
                              value={sanitizeHTMLSpace(order.Description)}
                              readOnly
                            />
                            <div className="motif-container sku-detail-container">
                              <div className="motif-row sku-detail-row">
                                <div className="motif-col-lg-4">
                                  <div className="label">Status</div>
                                  <div className="value">
                                    {order.StatusName}
                                  </div>
                                </div>
                                <div className="motif-col-lg-4">
                                  <div className="label">Cost</div>
                                  <div className="value">
                                    {getTotalOrderAmount(order)}
                                  </div>
                                </div>
                                <div className="motif-col-lg-4">
                                  <div className="label">Returnable</div>
                                  <div className="value">
                                    {order.IsReturnable ? "Yes" : "No"}
                                  </div>
                                </div>
                              </div>
                              <div className="motif-row sku-detail-row">
                                <div className="motif-col-lg-4">
                                  <div className="label">Cost Interval</div>
                                  <div className="value">
                                    {order.CostIntervalCd}
                                  </div>
                                </div>
                                <div className="motif-col-lg-4">
                                  <div className="label">Cost Type</div>
                                  <div className="value">
                                    {getCostCataegoryType(order)}
                                  </div>
                                </div>
                                <div className="motif-col-lg-4">
                                  <div className="label">Cost Metric</div>
                                  <div className="value">
                                    {getCostMetric(order)}
                                  </div>
                                </div>
                              </div>
                              <div className="motif-row sku-detail-row">
                                <div className="motif-col-lg-4">
                                  <div className="label">
                                    {COST_MACRO_CATEGORY}
                                  </div>
                                  <div className="value">
                                    {getCostMacroCategory(order)}
                                  </div>
                                </div>
                                <div className="motif-col-lg-4">
                                  <div className="label">NSR</div>
                                  <div className="value">
                                    {order.CostMetricNSR ?? 0}
                                  </div>
                                </div>
                                <div className="motif-col-lg-4"></div>
                              </div>
                            </div>
                            <div className="product-list-footer">
                              <span
                                onClick={() =>
                                  setOrders(
                                    data.map((o) =>
                                      o.OrderSkuId === order.OrderSkuId
                                        ? { ...o, isSKUExpanded: false }
                                        : o,
                                    ),
                                  )
                                }
                              >
                                Close Details
                              </span>
                            </div>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}

                  {/* Order Items */}
                  {order.isOrderExpanded && (
                    <tr className="product-list-wrapper">
                      <td
                        colSpan={columnsData.length}
                        className="order-items-list"
                        style={{ backgroundColor: "#131423" }}
                      >
                        <div className="product-list order-success-content">
                          <div className="product-list-content">
                            <table
                              className="motif-table-standard product-list-table"
                              style={
                                expandedTableWidth
                                  ? {
                                      width: `${expandedTableWidth}px`,
                                      maxWidth: `${expandedTableWidth}px`,
                                    }
                                  : undefined
                              }
                            >
                              <thead>
                                <tr>
                                  <th className="order-items-sku-col">
                                    <OrderItemsHeaderTooltip
                                      label="SKU name/SKU ID"
                                      type="sku"
                                    />
                                  </th>
                                  <th className="order-items-engagement-col">
                                    Engagement code
                                  </th>
                                  <th className="text-right-align order-items-unit-cost-col">
                                    <OrderItemsHeaderTooltip
                                      label="Unit cost"
                                      type="unitCost"
                                    />
                                  </th>
                                  <th className="order-items-units-col">
                                    <OrderItemsHeaderTooltip
                                      label="Units per period"
                                      type="unitsPerPeriod"
                                    />
                                  </th>
                                  <th className="order-items-periods-col">
                                    <OrderItemsHeaderTooltip
                                      label="No. of periods"
                                      type="numberOfPeriods"
                                    />
                                  </th>
                                  <th className="order-items-total-col">
                                    <OrderItemsHeaderTooltip
                                      label="Total cost"
                                      type="totalCost"
                                    />
                                  </th>
                                </tr>
                              </thead>
                              <tbody>
                                {order.OrderDetails?.map((detail: any) => (
                                  <ProductAccordian
                                    key={detail.ProductName}
                                    productName={detail.ProductName}
                                    skuDetails={detail.SkuDetails}
                                    showEngagementColumn={true}
                                    isOrderpipelineOrderTab={true}
                                    showQuantityColumn={false}
                                  />
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }
};

export default OrdersTable;
